import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../catalyst/dialog';
import { Button } from '../../catalyst/button';
import { Input } from '../../catalyst/input';
import { Select } from '../../catalyst/select';
import { roles, type Role } from '@avisoma/shared/dist/roles.js';
import backend from '../../services/backend';
import { PasswordRevealDialog } from './PasswordRevealDialog';

export function CreateUserDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [username, setUsername] = useState('');
  const [role, setRole] = useState<Exclude<Role, 'service'>>('user');
  const [reveal, setReveal] = useState<{ username: string; password: string } | null>(null);

  const create = useMutation({
    mutationFn: () => backend.createUser({ username: username.trim(), role }).then((res) => res.data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setReveal({ username: data.username, password: data.generatedPassword });
      setUsername('');
      setRole('user');
    },
  });

  const closeAll = () => {
    setReveal(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open && !reveal} onClose={onClose}>
        <DialogTitle>Create user</DialogTitle>
        <DialogBody>
          <div className="space-y-3">
            <div>
              <label htmlFor="create-user-username" className="block text-sm mb-1">Username</label>
              <Input
                id="create-user-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="alice"
                autoFocus
              />
            </div>
            <div>
              <label htmlFor="create-user-role" className="block text-sm mb-1">Role</label>
              {/* Exclude `service`: an account is never CREATED as DR-3681's machine identity. */}
              <Select id="create-user-role" value={role} onChange={(e) => setRole(e.target.value as Exclude<Role, 'service'>)}>
                {roles.filter((r) => r !== 'service').map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="blue"
            onClick={() => create.mutate()}
            disabled={create.isPending || username.trim().length === 0}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {reveal && (
        <PasswordRevealDialog
          open={true}
          username={reveal.username}
          password={reveal.password}
          title="User created"
          onClose={closeAll}
        />
      )}
    </>
  );
}
